import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Send, Loader2 } from "lucide-react";
import { CONTACT, CONTACT_SECTION } from "@/data/content";
import { Reveal } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", service: "", message: "" };

export default function Contact() {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    useEffect(() => {
        const handler = (e) => setForm((f) => ({ ...f, service: e.detail }));
        window.addEventListener("ign:prefill-service", handler);
        return () => window.removeEventListener("ign:prefill-service", handler);
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Merci de remplir les champs obligatoires.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/contact`, { ...form, source: "devis" });
            toast.success("Votre demande a bien été envoyée. Nous vous répondons sous 24h.");
            setForm(initial);
        } catch {
            toast.error("Une erreur est survenue. Merci de nous appeler directement.");
        } finally {
            setLoading(false);
        }
    };

    const inputClass =
        "w-full border border-line bg-white px-4 py-3 text-sm text-slate-800 focus:border-navy focus:outline-none focus:ring-1 focus:ring-electric/40 transition-all font-body";
    const labelClass = "block text-xs uppercase tracking-wider text-slate-400 mb-2 font-body";

    return (
        <section id="contact" className="pt-24 md:pt-32 pb-0 bg-white" data-testid="contact-section">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-5 gap-16">
                    <div className="lg:col-span-2">
                        <Reveal><span className="uppercase text-xs tracking-[0.2em] font-bold text-brand-red font-body">{CONTACT_SECTION.kicker}</span></Reveal>
                        <Reveal delay={0.05}>
                            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-navy mt-4 mb-6 font-display">
                                {CONTACT_SECTION.title[0]}<br />{CONTACT_SECTION.title[1]}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}><div className="h-px w-16 bg-brand-red mb-8" /></Reveal>
                        <Reveal delay={0.12}><p className="text-base leading-relaxed text-slate-500 mb-10 font-body">{CONTACT_SECTION.intro}</p></Reveal>

                        <div className="space-y-6">
                            <Reveal delay={0.16}>
                                <a href={`tel:${CONTACT.phone}`} className="flex items-center gap-4 group" data-testid="contact-phone">
                                    <div className="w-12 h-12 flex items-center justify-center bg-navy text-white group-hover:bg-brand-red transition-colors duration-300"><Phone className="w-5 h-5" /></div>
                                    <div><p className="text-xs text-slate-400 font-body">Téléphone</p><p className="text-sm font-semibold text-navy font-body">{CONTACT.phoneDisplay}</p></div>
                                </a>
                            </Reveal>
                            <Reveal delay={0.2}>
                                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 group" data-testid="contact-email">
                                    <div className="w-12 h-12 flex items-center justify-center bg-navy text-white group-hover:bg-brand-red transition-colors duration-300"><Mail className="w-5 h-5" /></div>
                                    <div><p className="text-xs text-slate-400 font-body">Email</p><p className="text-sm font-semibold text-navy font-body break-all">{CONTACT.email}</p></div>
                                </a>
                            </Reveal>
                            <Reveal delay={0.24}>
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 flex items-center justify-center bg-navy text-white"><MapPin className="w-5 h-5" /></div>
                                    <div><p className="text-xs text-slate-400 font-body">Adresse</p><p className="text-sm font-semibold text-navy font-body">{CONTACT.address}</p></div>
                                </div>
                            </Reveal>
                        </div>

                        <Reveal delay={0.3}>
                            <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" className="mt-10 inline-flex items-center gap-3 bg-[#25D366] text-white px-8 py-4 text-sm uppercase tracking-wide font-semibold hover:bg-[#1ea952] transition-colors duration-300 font-body" data-testid="contact-whatsapp">
                                <MessageCircle className="w-5 h-5" /> Contacter par WhatsApp
                            </a>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-3">
                        <Reveal y={40}>
                            <div className="bg-mist border border-line p-8 md:p-10">
                                <h3 className="text-xl font-medium text-navy mb-6 font-display">{CONTACT_SECTION.formTitle}</h3>
                                <form className="space-y-5" onSubmit={submit}>
                                    <div className="grid md:grid-cols-2 gap-5">
                                        <div>
                                            <label className={labelClass}>Nom complet *</label>
                                            <input data-testid="contact-name-input" required value={form.name} onChange={update("name")} className={inputClass} placeholder="Votre nom" type="text" />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Email *</label>
                                            <input data-testid="contact-email-input" required value={form.email} onChange={update("email")} className={inputClass} placeholder="votre@email.com" type="email" />
                                        </div>
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-5">
                                        <div>
                                            <label className={labelClass}>Téléphone</label>
                                            <input data-testid="contact-phone-input" value={form.phone} onChange={update("phone")} className={inputClass} placeholder="06 xx xx xx xx" type="tel" />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Service souhaité</label>
                                            <select data-testid="contact-service-select" value={form.service} onChange={update("service")} className={inputClass}>
                                                <option value="">Choisir un service</option>
                                                {CONTACT_SECTION.services.map((s) => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className={labelClass}>Message *</label>
                                        <textarea data-testid="contact-message-input" required value={form.message} onChange={update("message")} rows={5} className={inputClass + " resize-none"} placeholder="Décrivez votre besoin : type de local, surface, fréquence souhaitée..." />
                                    </div>
                                    <button data-testid="contact-submit-btn" type="submit" disabled={loading} className="w-full bg-navy text-white px-8 py-4 text-sm uppercase tracking-wide font-semibold hover:bg-brand-red transition-colors duration-300 flex items-center justify-center gap-2 font-body disabled:opacity-60">
                                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                        {loading ? "Envoi en cours..." : "Envoyer ma demande"}
                                    </button>
                                </form>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}
